import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { colorDePersona, inicialsDePersona } from '../utils/presenciaColors';

const CollaborationStatus = ({ 
  isConnected, 
  activeUsers, 
  activeEditors, 
  connectionError 
}) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [isExpanded, setIsExpanded] = React.useState(false);

  // Es treu el filtre per organització que hi havia aquí: la connexió ja és per
  // cas i l'accés ja s'ha comprovat al servidor, o sigui que qui és aquí hi té
  // dret. Filtrar a més per organització feia que en un cas amb gent de centres
  // diferents —un instructor d'un lloc i analistes d'un altre— no es veiessin
  // entre ells encara que hi fossin tots.
  const otherUsers = (activeUsers || []).filter(user => user.user_id !== currentUser?.id);
  const totalOtherUsers = otherUsers.length;

  // A quina pantalla del cas és cadascú. La dada ja arribava del servidor
  // (`user_presence_update`) i no es pintava enlloc.
  const nomDeSeccio = (seccio) => {
    if (!seccio) return null;
    const claus = {
      'strategic-environment': 'cases.strategicEnvironment',
      'strategy-mapping': 'cases.strategyMapping',
      'scenario-building': 'cases.scenarioBuilding',
      'overview': 'cases.overview',
      'notes': 'cases.notes'
    };
    if (claus[seccio]) return t(claus[seccio]);
    const passa = String(seccio).match(/^step_(\d)$/);
    if (passa) return t('collaboration.atStep', { number: passa[1] });
    return String(seccio).replace(/[-_]/g, ' ');
  };

  const getConnectionStatusColor = () => {
    if (connectionError) return 'bg-error-500';
    if (isConnected && totalOtherUsers > 0) return 'bg-success-500'; // Green when collaborating
    if (isConnected) return 'bg-neutral-400'; // Grey when working alone
    return 'bg-neutral-300'; // Light grey when disconnected
  };
  const hasActiveEditors = Object.keys(activeEditors || {}).length > 0;

  return (
    <div className="relative">
      {/* Compact Status Bar */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center space-x-2 bg-white border border-neutral-200 rounded-full px-3 py-2 hover:bg-neutral-50 transition-colors"
      >
        {/* Connection indicator */}
        <div className={`w-2 h-2 rounded-full ${getConnectionStatusColor()}`}></div>
        
        {/* User avatars - show up to 3 */}
        {totalOtherUsers > 0 && (
          <div className="flex -space-x-1">
            {otherUsers.slice(0, 3).map(user => (
              <div
                key={user.user_id}
                className="w-6 h-6 border-2 border-white rounded-full flex items-center justify-center"
                style={{ backgroundColor: colorDePersona(user.user_id) }}
                title={nomDeSeccio(user.current_section)
                  ? `${user.full_name} — ${nomDeSeccio(user.current_section)}`
                  : user.full_name}
              >
                <span className="text-white font-semibold text-[10px]">
                  {inicialsDePersona(user)}
                </span>
              </div>
            ))}
            {totalOtherUsers > 3 && (
              <div className="w-6 h-6 bg-neutral-100 border-2 border-white rounded-full flex items-center justify-center">
                <span className="text-neutral-600 font-medium text-xs">
                  +{totalOtherUsers - 3}
                </span>
              </div>
            )}
          </div>
        )}
        
        {/* Status text */}
        <span className="text-xs text-neutral-600">
          {connectionError ? t('collaboration.connectionError') : 
           totalOtherUsers > 0 ? t('collaboration.usersOnline', { count: totalOtherUsers + 1 }) :
           isConnected ? t('collaboration.workingAlone') : t('collaboration.connecting')}
        </span>

        {/* Expand indicator */}
        {(totalOtherUsers > 0 || hasActiveEditors || connectionError) && (
          <svg 
            className={`w-3 h-3 text-neutral-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </button>

      {/* Expanded Details */}
      {isExpanded && (totalOtherUsers > 0 || hasActiveEditors || connectionError) && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-neutral-200 rounded-lg shadow-lg p-4 z-50">
          {/* Connection Error */}
          {connectionError && (
            <div className="mb-3 p-2 bg-error-light rounded-md">
              <p className="text-xs text-error-dark">{connectionError}</p>
            </div>
          )}

          {/* Active Users */}
          {totalOtherUsers > 0 && (
            <div className="mb-3">
              <h4 className="text-xs font-medium text-neutral-700 mb-2">
                {t('collaboration.otherUsersOnline')}
              </h4>
              <div className="space-y-1">
                {otherUsers.map(user => (
                  <div key={user.user_id} className="flex items-center gap-2">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: colorDePersona(user.user_id) }}
                    >
                      <span className="text-white font-semibold text-[9px]">
                        {inicialsDePersona(user)}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-neutral-800 truncate">{user.full_name}</div>
                      {nomDeSeccio(user.current_section) && (
                        <div className="text-[11px] text-neutral-500 truncate">
                          {nomDeSeccio(user.current_section)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Active Editors */}
          {hasActiveEditors && (
            <div>
              <h4 className="text-xs font-medium text-neutral-700 mb-2">
                {t('collaboration.currentlyEditing')}
              </h4>
              <div className="space-y-1">
                {Object.entries(activeEditors || {}).map(([section, editor]) => (
                  <div key={section} className="flex items-center justify-between text-xs">
                    <span className="text-neutral-600 capitalize truncate">
                      {section.replace('_', ' ')}
                    </span>
                    <span className="text-accent-600 font-medium truncate ml-2">
                      {editor.full_name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CollaborationStatus;